/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import classNames from 'classnames';
import PropTypes from "prop-types";
import React, { Component } from 'react';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiI18n } from '../i18n';
import { EuiPortal } from '../portal'; // Exported for testing

export var paddingSizeToClassNameMap = {
  none: null,
  s: 'euiBottomBar--paddingSmall',
  m: 'euiBottomBar--paddingMedium',
  l: 'euiBottomBar--paddingLarge'
};
export class EuiBottomBar extends Component {
  bar = null;

  componentDidMount() {
    const height = this.bar ? this.bar.clientHeight : -1;
    document.body.style.paddingBottom = `${height}px`;

    if (this.props.bodyClassName) {
      document.body.classList.add(this.props.bodyClassName);
    }
  }

  componentWillUnmount() {
    document.body.style.paddingBottom = '';

    if (this.props.bodyClassName) {
      document.body.classList.remove(this.props.bodyClassName);
    }
  }

  render() {
    const {
      children,
      className,
      paddingSize = 'm',
      bodyClassName,
      landmarkHeading,
      ...rest
    } = this.props;
    const classes = classNames('euiBottomBar', paddingSizeToClassNameMap[paddingSize], className);
    return <EuiPortal>
        <EuiI18n token="euiBottomBar.screenReaderHeading" default="Page level controls">
          {screenReaderHeading => // Though it would be better to use aria-labelledby than aria-label and not repeat the same string twice
        // A bug in voiceover won't list some landmarks in the rotor without an aria-label
        <section aria-label={landmarkHeading ? landmarkHeading : screenReaderHeading} className={classes} ref={node => {
          this.bar = node;
        }} {...rest}>
              <EuiScreenReaderOnly>
                <h2>
                  {landmarkHeading ? landmarkHeading : screenReaderHeading}
                </h2>
              </EuiScreenReaderOnly>
              {children}
            </section>}
        </EuiI18n>
        <EuiScreenReaderOnly>
          <p aria-live="assertive">
            {landmarkHeading ? <EuiI18n token="euiBottomBar.customScreenReaderAnnouncement" default="There is a new region landmark called {landmarkHeading} with page level controls at the end of the document." values={{
            landmarkHeading
          }} /> : <EuiI18n token="euiBottomBar.screenReaderAnnouncement" default="There is a new region landmark with page level controls at the end of the document." />}
          </p>
        </EuiScreenReaderOnly>
      </EuiPortal>;
  }

}
EuiBottomBar.propTypes = {
  /**
     * Optional class applied to the body class
     */
  bodyClassName: PropTypes.string,

  /**
     * Padding applied to the bar
     */
  paddingSize: PropTypes.oneOf(["none", "s", "m", "l"]),

  /**
     * Customize the screen reader heading that helps users find this control. Default is "Page level controls".
     */
  landmarkHeading: PropTypes.string,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiBottomBar.displayName = "EuiBottomBar"; // @ts-ignore

  EuiBottomBar.__docgenInfo = {
    "description": "",
    "displayName": "EuiBottomBar",
    "props": {
      "bodyClassName": {
        "defaultValue": null,
        "description": "Optional class applied to the body class",
        "name": "bodyClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "paddingSize": {
        "defaultValue": null,
        "description": "Padding applied to the bar",
        "name": "paddingSize",
        "required": false,
        "type": {
          "name": "\"none\" | \"s\" | \"m\" | \"l\" | undefined"
        }
      },
      "landmarkHeading": {
        "defaultValue": null,
        "description": "Customize the screen reader heading that helps users find this control. Default is \"Page level controls\".",
        "name": "landmarkHeading",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/bottom_bar/bottom_bar.tsx#EuiBottomBar"] = {
      docgenInfo: EuiBottomBar.__docgenInfo,
      name: "EuiBottomBar",
      path: "src/components/bottom_bar/bottom_bar.tsx#EuiBottomBar"
    };
} catch (__react_docgen_typescript_loader_error) {}